import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Globe } from "lucide-react";

const FALLBACK_LANGS = [
  { code: "eng", label: "English" },
  { code: "spa", label: "Español" },
];

// Map a browser `navigator.language` (BCP-47 like "es-ES", "fr", "pt-BR") to
// one of our 3-letter Open-Library-style codes. Returns null when the browser
// language isn't one we support, so the caller falls back to English.
function detectBrowserLang() {
  try {
    const raw = (navigator.language || navigator.userLanguage || "").toLowerCase();
    if (!raw) return null;
    const primary = raw.split("-")[0];
    // Only English and Spanish are supported — everything else falls through
    // to the default (English).
    const map = { en: "eng", es: "spa" };
    return map[primary] || null;
  } catch {
    return null;
  }
}

/**
 * A single language picker. When the user changes it:
 *   1. localStorage is updated so anonymous browsers persist the choice.
 *   2. Any book searches automatically re-filter (they read from localStorage).
 *   3. UI strings covered by `lib/i18n.js` re-render via a `readsLangChanged` event.
 *   4. If signed in, the user record is patched so it becomes the default on all devices.
 */
export function LanguagePicker() {
  const { user, refresh } = useAuth();
  const [langs, setLangs] = useState(FALLBACK_LANGS);
  const [code, setCode] = useState(() => {
    try { return localStorage.getItem("readsLang") || "eng"; } catch { return "eng"; }
  });

  useEffect(() => {
    api.get("/i18n/languages")
      .then(({ data }) => { if (Array.isArray(data.languages)) setLangs(data.languages); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (user && typeof user === "object" && user.preferred_language) {
      setCode(user.preferred_language);
      try { localStorage.setItem("readsLang", user.preferred_language); } catch { /* noop */ }
    }
  }, [user]);

  const onChange = async (e) => {
    const next = e.target.value;
    if (next === code) return;
    setCode(next);
    try { localStorage.setItem("readsLang", next); } catch { /* noop */ }
    // Broadcast so components using `useLang()` re-render immediately.
    try { window.dispatchEvent(new Event("readsLangChanged")); } catch { /* noop */ }
    const label = langs.find((l) => l.code === next)?.label || next;
    if (user && typeof user === "object") {
      try {
        await api.patch("/me/preferences", { preferred_language: next });
        await refresh?.();
        toast.success(`Language set to ${label}.`);
      } catch {
        toast.error("Could not save language preference.");
      }
    } else {
      toast.success(`Language set to ${label}.`);
    }
  };

  return (
    <label className="relative block max-w-[210px]" data-testid="language-picker-wrap">
      <Globe className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#4A4030]" strokeWidth={1.5} />
      <select
        value={code}
        onChange={onChange}
        className="appearance-none w-full bg-[#F2EAD3] border border-[rgba(10,10,10,0.12)] rounded-sm pl-8 pr-3 py-2 text-sm text-[#0A0A0A] focus:outline-none focus:ring-1 focus:ring-black focus:border-black cursor-pointer"
        data-testid="language-picker"
      >
        {langs.map((l) => (
          <option key={l.code} value={l.code}>{l.label}</option>
        ))}
      </select>
    </label>
  );
}

// Only these language codes are supported today — everything else must
// clamp back to English so we never send bogus language queries to Open
// Library (and never filter results down to zero because a user has a
// stale/removed preference stored locally).
const SUPPORTED_LANGS = new Set(["eng", "spa"]);

export function getStoredLang() {
  try {
    const stored = localStorage.getItem("readsLang");
    if (stored && SUPPORTED_LANGS.has(stored)) return stored;
    // No stored preference — auto-detect from the browser once, then persist
    // so we don't keep re-detecting on every page load.
    const detected = detectBrowserLang();
    if (detected && SUPPORTED_LANGS.has(detected)) {
      try { localStorage.setItem("readsLang", detected); } catch { /* noop */ }
      return detected;
    }
    // Overwrite any stale/removed lang code so we don't keep hitting this branch.
    try { localStorage.setItem("readsLang", "eng"); } catch { /* noop */ }
    return "eng";
  } catch { return "eng"; }
}
